import { useState } from 'react';

import type { GetMarketplacePropertySearchQueryVariables } from '__generated__/athena';

import { buildPropertyTypes } from '../search/utils';
import type { PropertySearchQuery } from './useSearchQueries';

const getInitialPropertyTypes = (queryParams: PropertySearchQuery) => {
  const hasAnyPropertyTypeParam =
    queryParams?.singleFamily !== undefined ||
    queryParams?.condo !== undefined ||
    queryParams?.townhome !== undefined;

  if (hasAnyPropertyTypeParam) {
    return {
      singleFamily: queryParams?.singleFamily ?? false,
      condo: queryParams?.condo ?? false,
      townhome: queryParams?.townhome ?? false,
    };
  }

  return { singleFamily: true, condo: true, townhome: true };
};

export const useSearchState = (queryParams: PropertySearchQuery) => {
  const initialPropertyTypes = getInitialPropertyTypes(queryParams);
  const [minBeds, setBedrooms] = useState(queryParams?.bedrooms || 0);
  const [minBaths, setBathrooms] = useState(queryParams?.bathrooms || 0);
  const [canSelfTour, setCanSelfTour] = useState<boolean | null>(null);
  const [minPrice, setMinPrice] = useState(queryParams?.minListPrice || null);
  const [maxPrice, setMaxPrice] = useState(queryParams?.maxListPrice || null);
  const [singleFamily, setSingleFamily] = useState(initialPropertyTypes.singleFamily);
  const [condo, setCondo] = useState(initialPropertyTypes.condo);
  const [townhome, setTownhome] = useState(initialPropertyTypes.townhome);
  const [noHoas, setNoHoas] = useState(queryParams?.noHoas || false);
  const [singleStory, setSingleStory] = useState(queryParams?.hasSingleStory || false);
  const [mustHavePool, setMustHavePool] = useState(queryParams?.hasPool || false);
  const [minYearBuilt, setMinYearBuilt] = useState(queryParams?.minYearBuilt || null);
  const [maxYearBuilt, setMaxYearBuilt] = useState(queryParams?.maxYearBuilt || null);
  const [minLotSize, setMinLotSize] = useState(queryParams?.minLotSize || null);
  const [maxLotSize, setMaxLotSize] = useState(queryParams?.maxLotSize || null);
  const [minSquareFootage, setMinSquareFootage] = useState(queryParams?.minSquareFootage || null);
  const [maxSquareFootage, setMaxSquareFootage] = useState(queryParams?.maxSquareFootage || null);
  const [pageNumber, setPageNumber] = useState(0);

  const resetPage = () => setPageNumber(0);

  const resetFilters = () => {
    setBathrooms(0);
    setBedrooms(0);
    setCanSelfTour(null);
    setCondo(true);
    setMaxLotSize(null);
    setMaxPrice(null);
    setMaxSquareFootage(null);
    setMaxYearBuilt(null);
    setMinLotSize(null);
    setMinPrice(null);
    setMinSquareFootage(null);
    setMinYearBuilt(null);
    setMustHavePool(false);
    setNoHoas(false);
    setPageNumber(0);
    setSingleFamily(true);
    setSingleStory(false);
    setTownhome(true);
  };

  const buildSearchPayload = (
    override?: Partial<PropertySearchQuery>,
  ): GetMarketplacePropertySearchQueryVariables => {
    const { vertices, zoneIds, market, ...restOverrides } = override || {};
    const propertyTypes = buildPropertyTypes(singleFamily, condo, townhome);

    let payload: GetMarketplacePropertySearchQueryVariables = {
      bathrooms: minBaths,
      bedrooms: minBeds,
      canSelfTour,
      hasPool: mustHavePool,
      maxListPrice: maxPrice,
      maxSquareFootage,
      maxYearBuilt,
      minListPrice: minPrice,
      minSquareFootage,
      minYearBuilt,
      propertyTypes,
      singleStoryOnly: singleStory,
      ...restOverrides,
    };

    // Location handling (mutually exclusive: vertices > zoneIds > market)
    if (override?.vertices?.length) {
      payload = { ...payload, vertices };
    } else if (override?.zoneIds) {
      payload = { ...payload, zoneIds };
    } else if (override?.market) {
      payload = { ...payload, market };
    }

    return payload;
  };

  const getCanonical = (): PropertySearchQuery => ({
    bedrooms: minBeds,
    bathrooms: minBaths,
    canSelfTour,
    minListPrice: minPrice,
    maxListPrice: maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    hasSingleStory: singleStory,
    hasPool: mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  });

  return {
    resetFilters,
    resetPage,
    buildSearchPayload,
    getCanonical,
    get: {
      minBeds,
      minBaths,
      canSelfTour,
      minPrice,
      maxPrice,
      singleFamily,
      condo,
      townhome,
      noHoas,
      singleStory,
      mustHavePool,
      minYearBuilt,
      maxYearBuilt,
      minLotSize,
      maxLotSize,
      minSquareFootage,
      maxSquareFootage,
      pageNumber,
    },
    set: {
      setBedrooms,
      setBathrooms,
      setCanSelfTour,
      setMinPrice,
      setMaxPrice,
      setSingleFamily,
      setCondo,
      setTownhome,
      setNoHoas,
      setSingleStory,
      setMustHavePool,
      setMinYearBuilt,
      setMaxYearBuilt,
      setMinLotSize,
      setMaxLotSize,
      setMinSquareFootage,
      setMaxSquareFootage,
      setPageNumber,
    },
  };
};
